import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Grid,
} from "@mui/material";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import { DEFAULT_USER_SRC } from "../../../../../utils/utils";
import { Appointment } from "../types/appointment.types";
import { getStatusConfig } from "../utils/appointmentMappers";
import {
  isPastAppointment as checkIsPastAppointment,
  formatAppointmentDateTime,
} from "../utils/dateFormatters";
import { AccessTimeOutlined, DoneAllOutlined } from "@mui/icons-material";

interface AppointmentCardProps {
  appointment: Appointment;
  isPast?: boolean;
  isPending?: boolean;
  onCancel?: (appointmentId: string) => void;
  onReschedule?: (appointmentId: string) => void;
  onJoinVisit?: (patientJoinLink?: string) => void;
  onCompleteIntakeForm?: (appointmentId: string) => void;
  onViewSummary?: (appointmentId: string) => void;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  isPast,
  isPending,
  onCancel,
  onReschedule,
  onJoinVisit,
  onCompleteIntakeForm: _onCompleteIntakeForm,
  onViewSummary: _onViewSummary,
}) => {
  const statusConfig = getStatusConfig(appointment.status);
  const isPastAppointment =
    isPast ?? checkIsPastAppointment(appointment.dateTime);
  // Join Visit shows only for joinable (telehealth) appointments — i.e. those
  // with a patientJoinLink — that are confirmed. In-person visits have a null
  // join link, so the button is correctly hidden. The join link is used as the
  // mode signal because it's reliable across screens, unlike the mode string.
  const isJoinVisitEnabled =
    !!appointment.patientJoinLink &&
    (appointment.appStatusForPatient === "CONFIRMED" ||
      appointment.status === "Confirmed" ||
      (appointment.status as string) === "Checked In");
  // console.log("appointmente", appointment);
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #E0E0E0",
        borderRadius: 2,
        marginBottom: 2.5,
        transition: "all 0.2s ease-in-out",
        backgroundColor: "#FFFFFF",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            src={appointment.provider.avatar || DEFAULT_USER_SRC}
            alt={appointment.provider.name}
            sx={{ width: 56, height: 56 }}
          />
        }
        title={
          <Flex gap={2} alignItems="flex-start">
            <Flex flexDirection="column" gap={0.5}>
              <Text fontWeight="medium" fontSize="16px" color="#004186">
                {appointment.provider.name}
              </Text>
              <Text fontSize="14px" color="#1A1A1A99">
                {appointment.provider.specialty}
              </Text>
            </Flex>
            <Chip
              label={isPending ? "Pending Confirmation" : appointment.status}
              size="small"
              sx={{
                backgroundColor: isPending ? "#FFF4E5" : statusConfig.bgColor,
                color: isPending ? "#E58E26" : statusConfig.color,
                fontSize: "12px",
                fontWeight: 500,
                height: "28px",
                borderRadius: "14px",
                alignSelf: "flex-start",
                marginTop: 0.5,
              }}
            />
          </Flex>
        }
        action={
          <Flex
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {!isPastAppointment ? (
              <>
                {appointment.intakeComplete ? (
                  <Flex alignItems="center" gap={0.5}>
                    <DoneAllOutlined sx={{ fontSize: 14, color: "#00B917" }} />
                    <Text fontSize="12px" color="#00B917" fontWeight="medium">
                      Intake Form Completed
                    </Text>
                  </Flex>
                ) : appointment.intakeForms ? (
                  <Text
                    fontSize="12px"
                    color="#0097F0"
                    fontWeight="medium"
                    sx={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: "#0080D6",
                      },
                    }}
                    onClick={() => _onCompleteIntakeForm?.(appointment.uuid)}
                  >
                    Complete Intake Form
                  </Text>
                ) : (
                  <Text fontSize="12px" color="#1A1A1A99" whiteSpace="nowrap">
                    No intake form assigned
                  </Text>
                )}
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={() => _onViewSummary?.(appointment.uuid) || undefined}
                color="primary"
              >
                View Summary
              </Button>
            )}
          </Flex>
        }
        sx={{
          padding: 1.5,
          paddingBottom: 2,
          bgcolor: "#F2F6F8",
          alignItems: "flex-start",
          "& .MuiCardHeader-avatar": {
            marginRight: 2,
          },
          "& .MuiCardHeader-content": {
            flex: "1 1 auto",
          },
          "& .MuiCardHeader-action": {
            margin: 0,
            alignSelf: "flex-start",
          },
        }}
      />
      <CardContent sx={{ paddingTop: 0, paddingX: 2.5, paddingBottom: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Flex flexDirection="column" gap={0.5}>
              <Text color="text.secondary">Date & Time</Text>
              <Text color="text.primary">
                {formatAppointmentDateTime(
                  appointment.dateTime,
                  appointment.duration
                )}
              </Text>
            </Flex>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Flex flexDirection="column" gap={0.5}>
              <Text color="text.secondary">Appointment Mode</Text>
              <Text color="text.primary">
                {appointment.mode === "Telehealth" ? "Telehealth" : "In-person"}
              </Text>
            </Flex>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Flex flexDirection="column" gap={0.5}>
              <Text color="text.secondary">Location</Text>
              <Text color="text.primary">{appointment.location || "-"}</Text>
            </Flex>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Flex flexDirection="column" gap={0.5}>
              <Text color="text.secondary">Reason for Visit</Text>
              <Text color="text.primary">{appointment.reason}</Text>
            </Flex>
          </Grid>
        </Grid>
      </CardContent>
      {isPending && (
        <Flex
          alignItems="center"
          gap={0.5}
          sx={{
            paddingX: 2.5,
            paddingY: 1.25,
            borderTop: "1px solid #F0F0F0",
          }}
        >
          <AccessTimeOutlined sx={{ fontSize: 16, color: "#E58E26" }} />
          <Text fontSize="13px" color="#E58E26" fontWeight="medium">
            Awaiting Provider Confirmation
          </Text>
        </Flex>
      )}
      {!isPastAppointment && (
        <CardActions
          sx={{
            justifyContent: "flex-end",
            gap: 1,
            borderTop: "1px solid #F0F0F0",
          }}
        >
          <Button
            variant="text"
            onClick={() => onCancel?.(appointment.uuid)}
            color="error"
            sx={{
              color: "error.main",
              "&:hover": { color: "error.dark" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => onReschedule?.(appointment.uuid)}
            color="primary"
          >
            Reschedule
          </Button>
          {isJoinVisitEnabled && (
            <Button
              variant="contained"
              onClick={() => onJoinVisit?.(appointment?.patientJoinLink)}
              color="primary"
            >
              Join Visit
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default AppointmentCard;
